import {
  calculateSalesOfferPosition,
  calculateSalesOfferTotals,
  normalizeSalesOfferInput,
  normalizeSalesOfferSearchInput,
  normalizeSalesOpportunityInput,
  normalizeSalesOpportunitySearchInput,
  normalizeSalesOrderSearchInput,
  type CompleteSalesHandoffInput,
  type CreateSalesDeliveryInput,
  type DecideSalesOfferInput,
  type SalesCustomerOption,
  type SalesGateway,
  type SalesHandoffRecord,
  type SalesItemOption,
  type SalesMachineDetail,
  type SalesMachineOption,
  type SalesOfferPage,
  type SalesOfferRecord,
  type SalesOfferSearchInput,
  type SalesOpportunityPage,
  type SalesOpportunityRecord,
  type SalesOpportunitySearchInput,
  type SalesOrderPage,
  type SalesOrderRecord,
  type SalesOrderSearchInput,
  type SalesOverview,
  type SaveSalesOfferInput,
  type SaveSalesOpportunityInput,
  type SaveSalesOrderDeliveryInput,
} from '@forq/sales-data';

interface SalesTenantState {
  readonly opportunities: SalesOpportunityRecord[];
  readonly offers: SalesOfferRecord[];
  readonly orders: SalesOrderRecord[];
  readonly handoffs: SalesHandoffRecord[];
  /** Maschinen, die ein gewonnener Verkauf aus dem Bestand genommen hat -- verhindert den Doppelverkauf. */
  readonly soldMachineIds: string[];
  opportunityCounter: number;
  offerCounter: number;
  orderCounter: number;
  deliveryCounter: number;
}

function clone<T>(value: T): T { return structuredClone(value); }
function now(): string { return new Date().toISOString(); }
function prefixFor(tenantId: string): '1' | '2' | '3' {
  if (tenantId === 'stw' || tenantId.startsWith('2222')) return '2';
  if (tenantId === 'gsl' || tenantId.startsWith('3333')) return '3';
  return '1';
}
function euro(value: number): number { return Math.round(value * 100) / 100; }
function businessCase(counter: number): string { return `2026${String(counter).padStart(5, '0')}`; }
function offerNumber(counter: number): string { return `ANG-2026-${String(counter).padStart(5, '0')}`; }
function orderNumber(counter: number): string { return `AUF-2026-${String(counter).padStart(5, '0')}`; }
// Gemeinsame firmenweite Lieferschein-Serie (im Demo eigenständig, in Supabase geteilt mit den Vorgängen).
function deliveryNumber(counter: number): string { return `LS-2026-${String(counter).padStart(5, '0')}`; }

function customersFor(tenantId: string): readonly SalesCustomerOption[] {
  const prefix = prefixFor(tenantId);
  const values = prefix === '2'
    ? [
        ['Westfalen Schwerlast GmbH', 'Werl', 'Andreas Becker'],
        ['Meyer Maschinenlogistik KG', 'Soest', 'Sarah Meyer'],
      ]
    : prefix === '3'
      ? [
          ['Sauerland Holztechnik GmbH', 'Arnsberg', 'Daniel Schulte'],
          ['Metallbau Arnsberg KG', 'Arnsberg', 'Nina Kramer'],
        ]
      : [
          ['Werl Industriebedarf GmbH', 'Werl', 'Martin Schulz'],
          ['Bäckerei Haverkamp GmbH', 'Soest', 'Julia Haverkamp'],
        ];
  return values.map(([displayName, city, contactName], index) => ({
    id: `${tenantId}-sales-customer-${index + 1}`,
    number: `${prefix}${String(index + 1).padStart(4, '0')}`,
    displayName,
    city,
    contactName,
    email: `${contactName.split(' ')[0].toLocaleLowerCase('de')}@example.test`,
    phone: `02922 90${index + 1}0`,
  }));
}

function machinesFor(tenantId: string): readonly SalesMachineOption[] {
  const prefix = prefixFor(tenantId);
  return [
    {
      id: `${tenantId}-sales-machine-1`, number: `${prefix}0001`, manufacturer: 'Toyota', model: '8FBE16T',
      serialNumber: `TOY-${prefix}001`, status: 'stock', customerId: null,
      typeName: 'Elektro-Stapler',
      attributes: { capacity: '1600', liftHeight: '3300', overallHeight: '2100', drive: 'Elektro', hours: '4200' },
      salesPrice: 18500, purchasePrice: 13500,
    },
    {
      id: `${tenantId}-sales-machine-2`, number: `${prefix}0002`, manufacturer: 'Jungheinrich', model: 'ETV 225',
      serialNumber: `JH-${prefix}002`, status: 'stock', customerId: null,
      typeName: 'Schubmaststapler',
      attributes: { capacity: '2500', liftHeight: '8500', drive: 'Elektro', hours: '6100' },
      salesPrice: 24900, purchasePrice: 18000,
    },
  ];
}

function itemsFor(tenantId: string): readonly SalesItemOption[] {
  const prefix = prefixFor(tenantId);
  return [
    { id: `${tenantId}-sales-item-1`, number: `${prefix}0101`, kind: 'article', name: 'Seitenschieber 2A', unit: 'Stück', salesPrice: 1850, purchasePrice: 1220, vatRate: 19 },
    { id: `${tenantId}-sales-item-2`, number: `${prefix}0102`, kind: 'article', name: 'Ladegerät 48 V', unit: 'Stück', salesPrice: 2400, purchasePrice: 1750, vatRate: 19 },
    { id: `${tenantId}-sales-item-3`, number: `${prefix}0201`, kind: 'service', name: 'UVV-Prüfung', unit: 'Pauschale', salesPrice: 180, purchasePrice: 0, vatRate: 19 },
    { id: `${tenantId}-sales-item-4`, number: `${prefix}0202`, kind: 'service', name: 'Anlieferung', unit: 'Pauschale', salesPrice: 490, purchasePrice: 0, vatRate: 19 },
  ];
}

function opportunityFrom(
  tenantId: string,
  number: string,
  input: SaveSalesOpportunityInput,
  current?: SalesOpportunityRecord,
): SalesOpportunityRecord {
  // Prefer a customer picked from (or created in) the real customer base — number + name arrive
  // directly. Fall back to the demo customer list when only a customerId is given (used by tests).
  let customer: SalesCustomerOption | null = null;
  if (!input.customerNumber && input.customerId) {
    customer = customersFor(tenantId).find((item) => item.id === input.customerId) ?? null;
    if (!customer) throw new Error('Der ausgewählte Kunde gehört nicht zu diesem Mandanten.');
  }
  const timestamp = now();
  const expectedValue = input.expectedValue ?? 0;
  const probability = input.probability ?? 0;
  return {
    id: current?.id ?? `${tenantId}-opportunity-${crypto.randomUUID()}`,
    tenantId,
    number,
    customerId: customer?.id ?? input.customerId ?? null,
    customerNumber: input.customerNumber || customer?.number || '',
    customerName: input.customerName || customer?.displayName || '',
    prospectName: input.prospectName ?? '',
    contactName: input.contactName || customer?.contactName || '',
    email: input.email || customer?.email || '',
    phone: input.phone || customer?.phone || '',
    title: input.title,
    source: input.source ?? 'other',
    stage: input.stage ?? current?.stage ?? 'new',
    expectedValue,
    probability,
    weightedValue: euro(expectedValue * probability / 100),
    nextActionDate: input.nextActionDate ?? null,
    nextAction: input.nextAction ?? '',
    notes: input.notes ?? '',
    lostReason: current?.lostReason ?? '',
    offerCount: current?.offerCount ?? 0,
    currentOfferId: current?.currentOfferId ?? null,
    createdAt: current?.createdAt ?? timestamp,
    updatedAt: timestamp,
  };
}

function createSeed(tenantId: string): SalesTenantState {
  const customer = customersFor(tenantId)[0];
  const machine = machinesFor(tenantId)[0];
  const service = itemsFor(tenantId).find((item) => item.kind === 'service')!;
  const createdAt = '2026-07-12T09:00:00.000Z';
  const opportunity: SalesOpportunityRecord = {
    id: `${tenantId}-opportunity-seed`, tenantId, number: businessCase(132),
    customerId: customer.id, customerNumber: customer.number, customerName: customer.displayName,
    prospectName: '', contactName: customer.contactName, email: customer.email, phone: customer.phone,
    title: 'Elektrostapler für neue Halle', source: 'phone', stage: 'offer-presented',
    expectedValue: 28650, probability: 70, weightedValue: 20055,
    nextActionDate: '2026-07-15', nextAction: 'Telefonisch nachfassen',
    notes: 'Kunde benötigt geringe Bauhöhe und Triplexmast.', lostReason: '', offerCount: 1,
    currentOfferId: `${tenantId}-offer-seed`, createdAt, updatedAt: createdAt,
  };
  const positionInputs = [
    { kind: 'machine' as const, sourceId: machine.id, sourceNumber: machine.number, machineId: machine.id, description: `${machine.manufacturer} ${machine.model}`, quantity: 1, unit: 'Stück', unitPrice: 27500, purchasePrice: 21500, discountPercent: 2, vatRate: 19 },
    { kind: 'service' as const, sourceId: service.id, sourceNumber: service.number, description: service.name, quantity: 1, unit: service.unit, unitPrice: service.salesPrice, purchasePrice: service.purchasePrice, discountPercent: 0, vatRate: service.vatRate },
  ];
  const positions = positionInputs.map((position, index) => calculateSalesOfferPosition(position, index + 1, `${tenantId}-offer-position-${index + 1}`));
  const totals = calculateSalesOfferTotals(positions);
  const offer: SalesOfferRecord = {
    id: `${tenantId}-offer-seed`, tenantId, number: offerNumber(1), opportunityId: opportunity.id,
    opportunityNumber: opportunity.number, customerId: customer.id, customerNumber: customer.number,
    customerName: customer.displayName, prospectName: '', title: 'Paketangebot Elektrostapler', channel: 'phone',
    status: 'presented', validUntil: '2026-07-31', notes: 'Telefonisch vorgestellt; schriftliche Ausfertigung folgt.', currency: 'EUR',
    ...totals, positions, decisionReason: '', decidedAt: null, salesOrderId: null, createdAt, updatedAt: createdAt,
  };
  return {
    opportunities: [opportunity],
    offers: [offer],
    orders: [],
    handoffs: [],
    soldMachineIds: [],
    opportunityCounter: 133,
    offerCounter: 2,
    orderCounter: 1,
    deliveryCounter: 1,
  };
}

export class DemoSalesGateway implements SalesGateway {
  private readonly states = new Map<string, SalesTenantState>();

  private state(tenantId: string): SalesTenantState {
    const existing = this.states.get(tenantId);
    if (existing) return existing;
    const created = createSeed(tenantId);
    this.states.set(tenantId, created);
    return created;
  }

  async getOverview(tenantId: string): Promise<SalesOverview> {
    const state = this.state(tenantId);
    const openStages = new Set(['new', 'qualification', 'needs-confirmed', 'offer-planned', 'offer-presented', 'follow-up']);
    const openOpportunities = state.opportunities.filter((item) => openStages.has(item.stage));
    const today = new Date().toISOString().slice(0, 10);
    const month = new Date().toISOString().slice(0, 7);
    const wonOrders = state.orders.filter((item) => item.createdAt.startsWith(month));
    return {
      openOpportunities: openOpportunities.length,
      pipelineValue: euro(openOpportunities.reduce((sum, item) => sum + item.expectedValue, 0)),
      weightedPipelineValue: euro(openOpportunities.reduce((sum, item) => sum + item.weightedValue, 0)),
      followUpsDue: openOpportunities.filter((item) => item.nextActionDate && item.nextActionDate <= today).length,
      openOffers: state.offers.filter((item) => ['draft', 'presented', 'follow-up'].includes(item.status)).length,
      wonThisMonth: wonOrders.length,
      wonValueThisMonth: euro(wonOrders.reduce((sum, item) => sum + item.netTotal, 0)),
      pendingHandoffs: state.handoffs.filter((item) => item.status === 'pending').length,
    };
  }

  async searchCustomerOptions(tenantId: string, query = ''): Promise<readonly SalesCustomerOption[]> {
    const needle = query.trim().toLocaleLowerCase('de');
    return clone(customersFor(tenantId).filter((item) => !needle || [item.number, item.displayName, item.city, item.contactName]
      .some((value) => value.toLocaleLowerCase('de').includes(needle))));
  }

  async searchMachineOptions(tenantId: string, query = ''): Promise<readonly SalesMachineOption[]> {
    const needle = query.trim().toLocaleLowerCase('de');
    const sold = new Set(this.state(tenantId).soldMachineIds);
    return clone(machinesFor(tenantId)
      .filter((item) => !sold.has(item.id))
      .filter((item) => !needle || [item.number, item.manufacturer, item.model, item.serialNumber, item.typeName, ...Object.values(item.attributes)]
        .some((value) => value.toLocaleLowerCase('de').includes(needle))));
  }

  async getMachineDetail(tenantId: string, machineId: string): Promise<SalesMachineDetail | null> {
    const machine = machinesFor(tenantId).find((item) => item.id === machineId);
    if (!machine) return null;
    const labels: Record<string, { readonly label: string; readonly unit: string }> = {
      capacity: { label: 'Tragfähigkeit', unit: 'kg' },
      liftHeight: { label: 'Hubhöhe', unit: 'mm' },
      overallHeight: { label: 'Bauhöhe', unit: 'mm' },
      drive: { label: 'Antrieb', unit: '' },
      hours: { label: 'Betriebsstunden', unit: 'h' },
    };
    const specs = Object.entries(labels)
      .filter(([key]) => machine.attributes[key])
      .map(([key, meta]) => ({ label: meta.label, value: String(machine.attributes[key]), unit: meta.unit }));
    return {
      id: machine.id,
      number: machine.number,
      manufacturer: machine.manufacturer,
      model: machine.model,
      serialNumber: machine.serialNumber,
      constructionYear: machine.id.endsWith('machine-1') ? 2024 : 2022,
      typeName: machine.typeName,
      imageUrl: '',
      notes: machine.id.endsWith('machine-1') ? 'Seitenschieber, Vollkabine, LED-Arbeitsscheinwerfer' : '',
      specs,
    };
  }

  async searchItemOptions(tenantId: string, query = ''): Promise<readonly SalesItemOption[]> {
    const needle = query.trim().toLocaleLowerCase('de');
    return clone(itemsFor(tenantId).filter((item) => !needle || [item.number, item.name, item.kind]
      .some((value) => value.toLocaleLowerCase('de').includes(needle))));
  }

  async searchOpportunities(input: SalesOpportunitySearchInput): Promise<SalesOpportunityPage> {
    const normalized = normalizeSalesOpportunitySearchInput(input);
    const needle = normalized.query.toLocaleLowerCase('de');
    const filtered = this.state(normalized.tenantId).opportunities
      .filter((item) => normalized.stage === 'all' || item.stage === normalized.stage)
      .filter((item) => !needle || [item.number, item.title, item.customerName, item.prospectName, item.contactName]
        .some((value) => value.toLocaleLowerCase('de').includes(needle)))
      .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));
    const start = (normalized.page - 1) * normalized.pageSize;
    return { items: clone(filtered.slice(start, start + normalized.pageSize)), total: filtered.length, page: normalized.page, pageSize: normalized.pageSize };
  }

  async getOpportunity(tenantId: string, opportunityId: string): Promise<SalesOpportunityRecord> {
    const found = this.state(tenantId).opportunities.find((item) => item.id === opportunityId);
    if (!found) throw new Error('Die Verkaufschance wurde in diesem Mandanten nicht gefunden.');
    return clone(found);
  }

  async saveOpportunity(rawInput: SaveSalesOpportunityInput): Promise<SalesOpportunityRecord> {
    const input = normalizeSalesOpportunityInput(rawInput);
    const state = this.state(input.tenantId);
    if (input.opportunityId) {
      const index = state.opportunities.findIndex((item) => item.id === input.opportunityId);
      if (index < 0) throw new Error('Die Verkaufschance wurde in diesem Mandanten nicht gefunden.');
      if (['won', 'lost'].includes(state.opportunities[index].stage)) throw new Error('Abgeschlossene Verkaufschancen können nicht mehr bearbeitet werden.');
      const updated = opportunityFrom(input.tenantId, state.opportunities[index].number, input, state.opportunities[index]);
      state.opportunities[index] = updated;
      return clone(updated);
    }
    const created = opportunityFrom(input.tenantId, businessCase(state.opportunityCounter++), input);
    state.opportunities.unshift(created);
    return clone(created);
  }

  async searchOffers(input: SalesOfferSearchInput): Promise<SalesOfferPage> {
    const normalized = normalizeSalesOfferSearchInput(input);
    const needle = normalized.query.toLocaleLowerCase('de');
    const filtered = this.state(normalized.tenantId).offers
      .filter((item) => normalized.status === 'all' || item.status === normalized.status)
      .filter((item) => !needle || [item.number, item.title, item.customerName, item.prospectName, item.opportunityNumber]
        .some((value) => value.toLocaleLowerCase('de').includes(needle)))
      .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));
    const start = (normalized.page - 1) * normalized.pageSize;
    return { items: clone(filtered.slice(start, start + normalized.pageSize)), total: filtered.length, page: normalized.page, pageSize: normalized.pageSize };
  }

  async getOffer(tenantId: string, offerId: string): Promise<SalesOfferRecord> {
    const found = this.state(tenantId).offers.find((item) => item.id === offerId);
    if (!found) throw new Error('Das Angebot wurde in diesem Mandanten nicht gefunden.');
    return clone(found);
  }

  async saveOffer(rawInput: SaveSalesOfferInput): Promise<SalesOfferRecord> {
    const input = normalizeSalesOfferInput(rawInput);
    const state = this.state(input.tenantId);
    const existing = input.offerId ? state.offers.find((item) => item.id === input.offerId) ?? null : null;
    if (input.offerId && !existing) throw new Error('Das Angebot wurde in diesem Mandanten nicht gefunden.');
    if (existing && ['won', 'lost'].includes(existing.status)) throw new Error('Entschiedene Angebote können nicht mehr bearbeitet werden.');
    const opportunity = input.opportunityId
      ? state.opportunities.find((item) => item.id === input.opportunityId) ?? null
      : null;
    if (input.opportunityId && !opportunity) throw new Error('Die Verkaufschance gehört nicht zu diesem Mandanten.');
    let customer: SalesCustomerOption | null = null;
    if (!input.customerNumber && input.customerId) {
      customer = customersFor(input.tenantId).find((item) => item.id === input.customerId) ?? null;
      if (!customer) throw new Error('Der Kunde gehört nicht zu diesem Mandanten.');
    }
    const resolvedCustomerNumber = input.customerNumber || customer?.number || '';
    if (opportunity && opportunity.customerNumber && resolvedCustomerNumber && opportunity.customerNumber !== resolvedCustomerNumber) {
      throw new Error('Angebot und Verkaufschance müssen denselben Kunden verwenden.');
    }
    const timestamp = now();
    const positions = input.positions.map((position, index) => calculateSalesOfferPosition(
      position,
      index + 1,
      existing?.positions[index]?.id ?? `${input.tenantId}-offer-position-${crypto.randomUUID()}`,
    ));
    const totals = calculateSalesOfferTotals(positions);
    const offer: SalesOfferRecord = {
      id: existing?.id ?? `${input.tenantId}-offer-${crypto.randomUUID()}`,
      tenantId: input.tenantId,
      number: existing?.number ?? offerNumber(state.offerCounter++),
      opportunityId: opportunity?.id ?? null,
      opportunityNumber: opportunity?.number ?? '',
      customerId: customer?.id ?? input.customerId ?? opportunity?.customerId ?? null,
      customerNumber: resolvedCustomerNumber || opportunity?.customerNumber || '',
      customerName: input.customerName || customer?.displayName || opportunity?.customerName || '',
      prospectName: input.prospectName || opportunity?.prospectName || '',
      title: input.title,
      channel: input.channel ?? 'document',
      status: input.status ?? 'draft',
      validUntil: input.validUntil ?? null,
      notes: input.notes ?? '',
      currency: 'EUR',
      ...totals,
      positions,
      decisionReason: existing?.decisionReason ?? '',
      decidedAt: existing?.decidedAt ?? null,
      salesOrderId: existing?.salesOrderId ?? null,
      createdAt: existing?.createdAt ?? timestamp,
      updatedAt: timestamp,
    };
    if (existing) state.offers[state.offers.findIndex((item) => item.id === existing.id)] = offer;
    else state.offers.unshift(offer);
    if (opportunity) {
      const index = state.opportunities.findIndex((item) => item.id === opportunity.id);
      state.opportunities[index] = {
        ...opportunity,
        stage: offer.status === 'draft' ? 'offer-planned' : offer.status === 'follow-up' ? 'follow-up' : 'offer-presented',
        expectedValue: offer.netTotal,
        weightedValue: euro(offer.netTotal * opportunity.probability / 100),
        offerCount: existing ? opportunity.offerCount : opportunity.offerCount + 1,
        currentOfferId: offer.id,
        updatedAt: timestamp,
      };
    }
    return clone(offer);
  }

  async decideOffer(input: DecideSalesOfferInput): Promise<SalesOrderRecord | null> {
    const state = this.state(input.tenantId);
    const offerIndex = state.offers.findIndex((item) => item.id === input.offerId);
    if (offerIndex < 0) throw new Error('Das Angebot wurde in diesem Mandanten nicht gefunden.');
    const offer = state.offers[offerIndex];
    if (offer.status === 'won' && offer.salesOrderId) {
      return clone(state.orders.find((item) => item.id === offer.salesOrderId) ?? null);
    }
    if (offer.status === 'lost') return null;
    if (['expired'].includes(offer.status)) throw new Error('Ein abgelaufenes Angebot muss vor der Entscheidung reaktiviert werden.');
    const reason = input.reason?.trim() ?? '';
    if (input.decision === 'lost' && reason.length < 3) throw new Error('Bitte einen nachvollziehbaren Verlustgrund angeben.');
    const timestamp = now();

    if (input.decision === 'lost') {
      state.offers[offerIndex] = { ...offer, status: 'lost', decisionReason: reason, decidedAt: timestamp, updatedAt: timestamp };
      if (offer.opportunityId) {
        const index = state.opportunities.findIndex((item) => item.id === offer.opportunityId);
        if (index >= 0) state.opportunities[index] = { ...state.opportunities[index], stage: 'lost', probability: 0, weightedValue: 0, lostReason: reason, updatedAt: timestamp };
      }
      return null;
    }

    const machineIds = offer.positions
      .filter((position) => position.kind === 'machine' && position.machineId)
      .map((position) => position.machineId as string);
    if (machineIds.some((id) => state.soldMachineIds.includes(id))) {
      throw new Error('Mindestens eine Maschine dieses Angebots ist bereits verkauft und kann nicht erneut verkauft werden.');
    }
    const orderId = `${input.tenantId}-sales-order-${crypto.randomUUID()}`;
    const orderNo = orderNumber(state.orderCounter++);
    const handoffs: SalesHandoffRecord[] = [
      {
        id: `${input.tenantId}-handoff-finance-${crypto.randomUUID()}`,
        tenantId: input.tenantId,
        salesOrderId: orderId,
        salesOrderNumber: orderNo,
        target: 'finance',
        status: 'pending',
        title: 'Verkauf zur Abrechnung vorbereiten',
        payload: { offerId: offer.id, offerNumber: offer.number, netTotal: offer.netTotal, grossTotal: offer.grossTotal },
        errorMessage: '',
        resultNumber: '',
        createdAt: timestamp,
        updatedAt: timestamp,
      },
    ];
    if (offer.positions.some((position) => position.kind === 'machine')) {
      handoffs.push({
        id: `${input.tenantId}-handoff-logistics-${crypto.randomUUID()}`,
        tenantId: input.tenantId,
        salesOrderId: orderId,
        salesOrderNumber: orderNo,
        target: 'logistics',
        status: 'pending',
        title: 'Auslieferung für Verkauf vorbereiten',
        payload: { offerId: offer.id, offerNumber: offer.number, machineIds: offer.positions.map((position) => position.machineId).filter(Boolean) },
        errorMessage: '',
        resultNumber: '',
        createdAt: timestamp,
        updatedAt: timestamp,
      });
    }
    const order: SalesOrderRecord = {
      id: orderId,
      tenantId: input.tenantId,
      number: orderNo,
      businessCaseNumber: offer.opportunityNumber || businessCase(state.opportunityCounter++),
      offerId: offer.id,
      offerNumber: offer.number,
      opportunityId: offer.opportunityId,
      customerId: offer.customerId,
      customerNumber: offer.customerNumber,
      customerName: offer.customerName,
      prospectName: offer.prospectName,
      title: offer.title,
      status: handoffs.some((item) => item.target === 'logistics') ? 'delivery-planning' : 'billing-ready',
      netTotal: offer.netTotal,
      vatTotal: offer.vatTotal,
      grossTotal: offer.grossTotal,
      delivery: null,
      positions: clone(offer.positions),
      handoffs,
      createdAt: timestamp,
      updatedAt: timestamp,
    };
    state.orders.unshift(order);
    state.handoffs.unshift(...handoffs);
    state.offers[offerIndex] = { ...offer, status: 'won', decisionReason: reason, decidedAt: timestamp, salesOrderId: order.id, updatedAt: timestamp };
    if (offer.opportunityId) {
      const index = state.opportunities.findIndex((item) => item.id === offer.opportunityId);
      if (index >= 0) state.opportunities[index] = { ...state.opportunities[index], stage: 'won', probability: 100, expectedValue: offer.netTotal, weightedValue: offer.netTotal, updatedAt: timestamp };
    }
    state.soldMachineIds.push(...machineIds);
    return clone(order);
  }

  async searchOrders(input: SalesOrderSearchInput): Promise<SalesOrderPage> {
    const normalized = normalizeSalesOrderSearchInput(input);
    const needle = normalized.query.toLocaleLowerCase('de');
    const filtered = this.state(normalized.tenantId).orders
      .filter((item) => !needle || [item.number, item.businessCaseNumber, item.offerNumber, item.title, item.customerName, item.prospectName]
        .some((value) => value.toLocaleLowerCase('de').includes(needle)))
      .sort((left, right) => right.createdAt.localeCompare(left.createdAt));
    const start = (normalized.page - 1) * normalized.pageSize;
    return { items: clone(filtered.slice(start, start + normalized.pageSize)), total: filtered.length, page: normalized.page, pageSize: normalized.pageSize };
  }

  async getOrder(tenantId: string, orderId: string): Promise<SalesOrderRecord> {
    const found = this.state(tenantId).orders.find((item) => item.id === orderId);
    if (!found) throw new Error('Der Verkaufsauftrag wurde in diesem Mandanten nicht gefunden.');
    return clone(found);
  }

  async listHandoffs(tenantId: string, limit = 100): Promise<readonly SalesHandoffRecord[]> {
    return clone(this.state(tenantId).handoffs.slice(0, Math.max(1, Math.min(500, limit))));
  }

  async completeHandoff(input: CompleteSalesHandoffInput): Promise<SalesHandoffRecord> {
    const state = this.state(input.tenantId);
    const index = state.handoffs.findIndex((item) => item.id === input.handoffId);
    if (index < 0) throw new Error('Die Übergabe wurde in diesem Mandanten nicht gefunden.');
    const timestamp = now();
    const updated: SalesHandoffRecord = {
      ...state.handoffs[index],
      status: 'completed',
      resultNumber: input.resultNumber?.trim() ?? '',
      updatedAt: timestamp,
    };
    state.handoffs[index] = updated;
    // Reflect the completion on the owning order (order.handoffs holds its own copies).
    const orderIndex = state.orders.findIndex((order) => order.id === updated.salesOrderId);
    if (orderIndex >= 0) {
      const order = state.orders[orderIndex];
      state.orders[orderIndex] = {
        ...order,
        handoffs: order.handoffs.map((handoff) => (handoff.id === updated.id ? updated : handoff)),
        updatedAt: timestamp,
      };
    }
    return clone(updated);
  }

  // Verwaltung bereitet die Auslieferung vor: LS-Nummer vergeben, Logistik-Uebergabe auf 'accepted'.
  // Der Auftrag bleibt 'delivery-planning', bis die Werkstatt die Durchsicht bestaetigt.
  async createDelivery(input: CreateSalesDeliveryInput): Promise<SalesOrderRecord> {
    const state = this.state(input.tenantId);
    const orderIndex = state.orders.findIndex((order) => order.id === input.orderId);
    if (orderIndex < 0) throw new Error('Der Verkaufsauftrag wurde in diesem Mandanten nicht gefunden.');
    const order = state.orders[orderIndex];
    const logistics = order.handoffs.find((handoff) => handoff.target === 'logistics');
    if (!logistics) throw new Error('Für diesen Auftrag steht keine Auslieferung an.');
    // Idempotent: LS-Nummer schon vergeben (Belege existieren) -> unveraendert zurueckgeben.
    if (logistics.resultNumber) return clone(order);
    if (order.status !== 'delivery-planning') throw new Error('Nur Aufträge in der Auslieferungsvorbereitung können Belege erhalten.');

    const timestamp = now();
    const number = deliveryNumber(state.deliveryCounter++);
    const prepared: SalesHandoffRecord = { ...logistics, status: 'accepted', resultNumber: number, updatedAt: timestamp };
    const updatedOrder: SalesOrderRecord = {
      ...order,
      handoffs: order.handoffs.map((handoff) => (handoff.id === logistics.id ? prepared : handoff)),
      updatedAt: timestamp,
    };
    state.orders[orderIndex] = updatedOrder;
    const globalIndex = state.handoffs.findIndex((handoff) => handoff.id === logistics.id);
    if (globalIndex >= 0) state.handoffs[globalIndex] = prepared;
    return clone(updatedOrder);
  }

  // Werkstatt bestaetigt die Auslieferungsdurchsicht: Uebergabe 'completed', Auftrag 'delivered'.
  async confirmDelivery(input: CreateSalesDeliveryInput): Promise<SalesOrderRecord> {
    const state = this.state(input.tenantId);
    const orderIndex = state.orders.findIndex((order) => order.id === input.orderId);
    if (orderIndex < 0) throw new Error('Der Verkaufsauftrag wurde in diesem Mandanten nicht gefunden.');
    const order = state.orders[orderIndex];
    if (order.status === 'delivered') return clone(order);
    if (order.status !== 'delivery-planning') throw new Error('Nur Aufträge in der Auslieferungsvorbereitung können ausgeliefert werden.');
    const logistics = order.handoffs.find((handoff) => handoff.target === 'logistics');
    if (!logistics) throw new Error('Für diesen Auftrag steht keine Auslieferung an.');
    if (logistics.status !== 'accepted') throw new Error('Vor der Auslieferung müssen Lieferschein und UVV-Prüfbuch erstellt werden.');

    const timestamp = now();
    const completedHandoff: SalesHandoffRecord = { ...logistics, status: 'completed', updatedAt: timestamp };
    const delivered: SalesOrderRecord = {
      ...order,
      status: 'delivered',
      handoffs: order.handoffs.map((handoff) => (handoff.id === logistics.id ? completedHandoff : handoff)),
      updatedAt: timestamp,
    };
    state.orders[orderIndex] = delivered;
    const globalIndex = state.handoffs.findIndex((handoff) => handoff.id === logistics.id);
    if (globalIndex >= 0) state.handoffs[globalIndex] = completedHandoff;
    return clone(delivered);
  }

  async setDeliveryDetails(input: SaveSalesOrderDeliveryInput): Promise<SalesOrderRecord> {
    const state = this.state(input.tenantId);
    const orderIndex = state.orders.findIndex((order) => order.id === input.orderId);
    if (orderIndex < 0) throw new Error('Der Verkaufsauftrag wurde in diesem Mandanten nicht gefunden.');
    const updated: SalesOrderRecord = { ...state.orders[orderIndex], delivery: input.delivery, updatedAt: now() };
    state.orders[orderIndex] = updated;
    return clone(updated);
  }

  /**
   * Entfernt einen Datensatz -- das Gegenstueck zum Loeschen in der Datenbank.
   * Ohne das meldet der Demo-Modus "geloescht", waehrend der Eintrag stehen bleibt.
   */
  removeOffer(tenantId: string, offerId: string): void {
    const zustand = this.states.get(tenantId);
    if (!zustand) return;
    const stelle = zustand.offers.findIndex((eintrag) => eintrag.id === offerId);
    if (stelle >= 0) zustand.offers.splice(stelle, 1);
  }
}

export function createDemoSalesGateway(): DemoSalesGateway { return new DemoSalesGateway(); }
